import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert } from '../components/ui/Alert'
import { Button } from '../components/ui/Button'
import { Card, CardHeader } from '../components/ui/Card'
import { Spinner } from '../components/ui/Spinner'
import { TranslationsEditor, type TranslationFieldDef, type TranslationsAdapter } from '../components/TranslationsEditor'
import {
  fetchContentTranslations,
  regenerateContentTranslation,
  saveContentTranslation,
  startTranslationBackfill,
  type ContentItem,
  type ContentKind,
  type ContentOverview,
} from '../services/translationService'
import { STORE_LANGUAGES } from '../utils/storeLanguages'
import { getUserFacingErrorMessage } from '../utils/userFacingError'

const GROUPS: Record<ContentKind, { title: string; hint: string; fields: TranslationFieldDef[] }> = {
  store: {
    title: 'Store details',
    hint: 'Your tagline, About text and opening hours.',
    fields: [
      { key: 'tagline', label: 'Tagline' },
      { key: 'aboutText', label: 'About text', multiline: true, rows: 6 },
      { key: 'businessHours', label: 'Opening hours', multiline: true, rows: 3 },
    ],
  },
  hero_slide: {
    title: 'Homepage banners',
    hint: 'The large slides at the top of your homepage.',
    fields: [
      { key: 'title', label: 'Headline' },
      { key: 'subtitle', label: 'Subtitle', multiline: true, rows: 2 },
      { key: 'ctaText', label: 'Button text' },
      { key: 'badge', label: 'Badge' },
    ],
  },
  trust_badge: {
    title: 'Trust badges',
    hint: 'The short reassurance lines under the banner.',
    fields: [
      { key: 'label', label: 'Title' },
      { key: 'sublabel', label: 'Description' },
    ],
  },
  offer: {
    title: 'Special offers',
    hint: 'Titles and descriptions of your deals.',
    fields: [
      { key: 'title', label: 'Title' },
      { key: 'subtitle', label: 'Description', multiline: true, rows: 2 },
      { key: 'badge', label: 'Badge' },
    ],
  },
  homepage_section: {
    title: 'Homepage sections',
    hint: 'Headings of the product rows on your homepage.',
    fields: [
      { key: 'title', label: 'Heading' },
      { key: 'subtitle', label: 'Subheading' },
    ],
  },
  faq: {
    title: 'Help Center questions',
    hint: 'The questions and answers on your Help Center page.',
    fields: [
      { key: 'category', label: 'Category' },
      { key: 'question', label: 'Question' },
      { key: 'answer', label: 'Answer', multiline: true, rows: 5 },
    ],
  },
}

function languageName(code: string): string {
  return STORE_LANGUAGES.find((l) => l.code === code)?.label.split(' (')[0] ?? code.toUpperCase()
}

const STATUS_DOT: Record<string, string> = {
  missing: 'bg-muted-foreground/40',
  auto: 'bg-primary',
  edited: 'bg-emerald-500',
  stale: 'bg-amber-500',
}

function ItemEditor({ item, fields }: { item: ContentItem; fields: TranslationFieldDef[] }) {
  const adapter = useMemo<TranslationsAdapter>(
    () => ({
      load: async () => {
        const o = await fetchContentTranslations()
        const found = o.groups.flatMap((g) => g.items).find((i) => i.kind === item.kind && i.ref === item.ref)
        if (!found) throw new Error('This item no longer exists.')
        return found
      },
      save: (locale, values) => saveContentTranslation(item.kind, item.ref, locale, values),
      regenerate: (locale) => regenerateContentTranslation(item.kind, item.ref, locale),
    }),
    [item.kind, item.ref],
  )
  return (
    <details className="group rounded-2xl border border-border bg-card">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm font-medium hover:bg-muted/50">
        <span className="line-clamp-1">{item.label}</span>
        <span className="flex shrink-0 items-center gap-3 text-xs font-normal text-muted-foreground">
          {item.targetLocales.map((l) => (
            <span key={l} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${STATUS_DOT[item.translations[l]?.status ?? 'missing']}`} aria-hidden />
              {l.toUpperCase()}
            </span>
          ))}
        </span>
      </summary>
      <div className="border-t border-border px-4 py-4">
        <TranslationsEditor embedded adapter={adapter} initialView={item} fields={fields} />
      </div>
    </details>
  )
}

/** One place to review how banners, badges, offers, sections and store text read in each language. */
export default function StoreContentTranslations() {
  const [overview, setOverview] = useState<ContentOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setOverview(await fetchContentTranslations())
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load store content'))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function translateMissing() {
    setStarting(true)
    setNotice(null)
    try {
      await startTranslationBackfill()
      setNotice('Translating in the background. Refresh in a minute to see the results.')
    } catch (e) {
      setNotice(getUserFacingErrorMessage(e, 'Could not start translating'))
    } finally {
      setStarting(false)
    }
  }

  if (error) {
    return (
      <Card>
        <CardHeader title="Store content translations" />
        <Alert variant="destructive" title="Could not load">
          {error}
        </Alert>
      </Card>
    )
  }
  if (!overview) {
    return (
      <Card>
        <CardHeader title="Store content translations" />
        <Spinner />
      </Card>
    )
  }

  const multiLanguage = overview.targetLocales.length > 0

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Store content translations"
          description={`Everything you wrote for your storefront, in each language you offer. Write in ${languageName(overview.sourceLocale)}; other languages are translated automatically and you can edit any of them. Products and categories are translated on their own pages.`}
          action={
            multiLanguage && overview.autoTranslationAvailable ? (
              <Button type="button" variant="secondary" size="sm" onClick={() => void translateMissing()} disabled={starting}>
                {starting ? 'Starting…' : 'Translate everything missing'}
              </Button>
            ) : null
          }
        />
        {!multiLanguage ? (
          <p className="text-sm text-muted-foreground">
            Your shop is shown in one language. Add more under{' '}
            <Link to="/settings" className="font-medium text-primary hover:underline">
              Settings → Storefront languages
            </Link>{' '}
            to translate your content.
          </p>
        ) : null}
        {notice ? (
          <p className="mt-3 text-sm text-muted-foreground" role="status">
            {notice}
          </p>
        ) : null}
        {multiLanguage && !overview.autoTranslationAvailable ? (
          <div className="mt-4">
            <Alert variant="info" title="Automatic translation is not set up yet">
              You can still write each language by hand. Shoppers see your original text wherever a translation is missing.
            </Alert>
          </div>
        ) : null}
      </Card>

      {multiLanguage
        ? overview.groups
            .filter((g) => g.items.length > 0)
            .map((g) => (
              <Card key={g.kind}>
                <CardHeader title={GROUPS[g.kind].title} description={GROUPS[g.kind].hint} />
                <div className="space-y-3">
                  {g.items.map((item) => (
                    <ItemEditor key={`${item.kind}-${item.ref}`} item={item} fields={GROUPS[g.kind].fields} />
                  ))}
                </div>
              </Card>
            ))
        : null}
    </div>
  )
}
